import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import operaCloudCompareService from '../services/operaCloudCompareService';
import './ProfileIATAPage.css';
import './OperaCloudCompare.css';

// Opera Cloud v Opera Cloud — list of saved comparison configurations.
// Each item pairs a source and a target OPERA Cloud instance. "New Comparison" runs the wizard.

const OperaCloudCompareListPage = () => {
    const navigate = useNavigate();
    const { isSuperAdmin } = useAuth();
    const { selectedAccount, selectedPartner } = useAccount();
    const [search, setSearch] = useState('');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await operaCloudCompareService.list({
                account_id: selectedAccount || undefined,
                partner_id: selectedPartner || undefined,
            });
            setItems(res.data?.comparisons || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load comparisons');
        } finally {
            setLoading(false);
        }
    }, [selectedAccount, selectedPartner]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm('Delete this comparison?')) return;
        try {
            await operaCloudCompareService.remove(id);
            setItems(prev => prev.filter(c => c.id !== id));
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete comparison');
        }
    };

    const filtered = items.filter(c =>
        !search ||
        (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.source_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.target_name || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="pi-page">
            <div className="pi-header">
                <div className="pi-header-left">
                    <button className="pi-back-btn" onClick={() => navigate('/apps')}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Apps
                    </button>
                    <div className="pi-title-block">
                        <h1 className="pi-title">Opera Cloud v Opera Cloud</h1>
                        <p className="pi-subtitle">Compare and repair configuration between two Oracle OPERA Cloud instances</p>
                    </div>
                </div>
                <div className="pi-header-actions">
                    <button className="pi-btn-add" onClick={() => navigate('/apps/opera-cloud-compare/new')}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        New Comparison
                    </button>
                </div>
            </div>

            <div className="pi-search-wrap">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="pi-search-icon">
                    <circle cx="7" cy="7" r="4.5" stroke="#9ca3af" strokeWidth="1.5"/>
                    <path d="M11 11l3 3" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                    type="text"
                    className="pi-search-input"
                    placeholder="Search by name or instance..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            {error && <div className="ppw-error-msg" style={{ margin: '8px 0' }}>{error}</div>}

            <div className="pi-list-panel">
                {loading ? (
                    <div className="pi-empty"><p className="pi-empty-text">Loading…</p></div>
                ) : filtered.length === 0 ? (
                    <div className="pi-empty">
                        <div className="pi-empty-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                <path d="M7.5 17a3.5 3.5 0 0 1-.5-6.96A4.5 4.5 0 0 1 15 9.5a3 3 0 0 1 .5 5.96" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M14.5 20a3 3 0 0 0 .35-5.98A3.75 3.75 0 0 0 8 12.6" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <p className="pi-empty-title">No comparisons yet</p>
                        <p className="pi-empty-text">Create a comparison to align configuration from a source OPERA Cloud instance onto a target.</p>
                        <button className="pi-btn-add" onClick={() => navigate('/apps/opera-cloud-compare/new')}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            New Comparison
                        </button>
                    </div>
                ) : (
                    <div className="pi-list">
                        {filtered.map(c => {
                            // Paid → open the comparison screen; otherwise resume the wizard at payment.
                            const open = () => navigate(
                                c.paid === 'Y'
                                    ? `/apps/opera-cloud-compare/${c.id}`
                                    : `/apps/opera-cloud-compare/${c.id}/pay`
                            );
                            return (
                                <div
                                    key={c.id}
                                    className="pi-list-item"
                                    onClick={open}
                                    onKeyDown={e => e.key === 'Enter' && open()}
                                    role="button"
                                    tabIndex={0}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="pi-item-main">
                                        <div className="pi-item-name">{c.name}</div>
                                        <div className="pi-item-sub">
                                            {c.source_name} → {c.target_name}
                                            {c.hotel_name ? ` · ${c.hotel_name}` : ''}
                                            {c.paid === 'Y' ? ' · Paid' : ' · Awaiting payment'}
                                        </div>
                                    </div>
                                    {isSuperAdmin && (
                                        <button className="occ-link-btn" onClick={(e) => handleDelete(e, c.id)}>Delete</button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OperaCloudCompareListPage;
